/**
 * Central auth service: single place for login/logout and session restore.
 * Redux remains the source of truth for currentUser; this module orchestrates
 * API calls and guards against races (e.g. late profile response after logout).
 */
import store from '../redux/store';
import history from '../history';
import swal from 'sweetalert';
import { signOut } from '../api/sessions';
import { getUserDetails } from '../api/userApi';
import { loginUser, logoutUser } from '../redux/actions';
import { setLoggingOut, isLoggingOut, logout as clearLegacyStorage } from '../helpers/authHelper';

const LOGOUT_COOLDOWN_MS = 500;

/**
 * @returns {object} current user from Redux (never null)
 */
export function getCurrentUser() {
	const state = store.getState();
	return (state.userStore && state.userStore.currentUser) || {};
}

/**
 * @returns {boolean} true if a user with email is in Redux
 */
export function isAuthenticated() {
	const user = getCurrentUser();
	return !!(user && user.email);
}

/**
 * Logout: call backend to clear cookie first; only then clear Redux and redirect.
 * If the API fails, we do not redirect so the cookie is not cleared and refresh would re-log in.
 */
export function logout() {
	setLoggingOut(true);

	signOut()
		.then(() => {
			store.dispatch(logoutUser());
			clearLegacyStorage();
			history.push('/');
		})
		.catch(() => {
			// Backend unreachable or error — don't redirect; user can retry. Cookie not cleared.
			swal('Logout failed', 'Could not reach the server. Cookie may still be set — try again or check that the backend is running.', 'error');
		})
		.finally(() => {
			setTimeout(() => setLoggingOut(false), LOGOUT_COOLDOWN_MS);
		});
}

/**
 * Restore session from cookie (GET /user/profile). Only updates Redux if:
 * - We're not in the middle of logout
 * - Response succeeds and we're still not logging out
 * Call once on app init and when entering a private route without user.
 * @returns {Promise<void>}
 */
export function restoreSession() {
	if (isLoggingOut()) return Promise.resolve();

	const user = getCurrentUser();
	if (user && user.email) return Promise.resolve();

	return getUserDetails()
		.then((response) => {
			if (!isLoggingOut() && response && response.data && response.data.user) {
				store.dispatch(loginUser(response.data.user));
			}
		})
		.catch(() => {});
}

/**
 * Always fetch latest user from server (GET /user/profile) and update Redux.
 * Use when entering admin/partner so UI reflects server state (email/role/vendor changes).
 * On 401 the interceptor will clear Redux and redirect; we don't update here.
 * @returns {Promise<void>}
 */
export function refreshUser() {
	if (isLoggingOut()) return Promise.resolve();

	return getUserDetails()
		.then((response) => {
			if (!isLoggingOut() && response && response.data && response.data.user) {
				store.dispatch(loginUser(response.data.user));
			}
		})
		.catch(() => {});
}
